import isEqual from 'lodash/isEqual';
import { createEarlyFetchCache } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/index.tsx';
import { fetchData } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/fetch/index.tsx';
import { waitForGlobalVariable } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/global-variable-polling/index.tsx';
import { getEarlyScriptObservabilityCache } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/observability/index.tsx';
import { getProjectIdFromMap } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/project-id-mapping/index.tsx';
import { getProjectKeyFromLocation } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/url-extractors/index.tsx';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { IssueTypesForProjectsResponse } from '../types.tsx';
import { getIssueTypesForProjectsUrl } from '../utils.tsx';

// Create a type-specific early fetch cache for issue types
const earlyFetchCache = createEarlyFetchCache<IssueTypesForProjectsResponse, ProjectId[]>({
	paramsEqualityFn: (a, b) => isEqual(a.map(String), b.map(String)),
});

/**
 * Gets the cached response and marks it as accessed
 * The cache will be automatically cleared after access or if stale
 */
export const getEarlyFetchResponse = (
	projectIds: ProjectId[],
): Promise<IssueTypesForProjectsResponse> | null => {
	return earlyFetchCache.getCachedResponse(projectIds);
};

/**
 * Helper function to fetch issue types with proper type
 */
const fetchIssueTypesForProjects = (
	projectIds: ProjectId[],
): Promise<IssueTypesForProjectsResponse> => {
	if (!projectIds.length) {
		throw new Error('No project ids provided');
	}

	const url = getIssueTypesForProjectsUrl(projectIds);

	return fetchData({ url, method: 'GET' });
};

/**
 * Fetches issue types data early based on the project IDs
 */
export const putIssueTypesForProjectsEarlyScriptResponse = (projectIds: ProjectId[]): void => {
	// Execute the actual fetch for issue types
	const response = fetchIssueTypesForProjects(projectIds);

	// Store the fetch promise in the cache
	earlyFetchCache.setCacheResponse(response, projectIds);
};

/**
 * Starts the early API request for issue types.
 * This function is executed before the main app loads.
 */
export function runGetIssueTypesForProjectsEarlyScript(location: Location): boolean {
	// Parse the location to extract the necessary parameters
	const projectKey = getProjectKeyFromLocation(location);
	if (!projectKey) {
		return false;
	}

	// Use cached projectId if available, otherwise wait for SPA_STATE
	getProjectIdFromMap(projectKey, waitForGlobalVariable)
		.then((projectId) => {
			if (projectId) {
				// For now, we fetch issue types for the current project
				// This can be extended to handle multiple projects if needed
				putIssueTypesForProjectsEarlyScriptResponse([String(projectId)]);
			}
		})
		.catch((error) => {
			getEarlyScriptObservabilityCache()?.recordError(
				'polaris.early-fetch-cache.project-id-mapping',
				'Error fetching issue types',
				error instanceof Error ? error : new Error(String(error)),
			);
		});

	return true;
}
